import React, { useState } from "react";
import { Checkbox, Field, Text } from "@fluentui/react-components";
import { useParams } from "react-router-dom";
import {
  type ConnectionEditState,
  type ConnectionEditStateUpdater,
  type Status,
  type SourceStatusMapping,
} from "@onetasklist/shared";
import {
  useJiraSourceSettingsText,
  useJiraSourceSettingsViewModel,
} from "@onetasklist/shared/sources/jira";
import { getSourceSettingsApiAccessor } from "@onetasklist/shared/sources/source-settings";
import { useApi } from "@api/useApi";
import { useTranslation } from "react-i18next";
import { SourceMultiTagPicker } from "./components/SourceMultiTagPicker";
import { SourceSingleTagPicker } from "./components/SourceSingleTagPicker";
import { SettingsSectionCard } from "./components/SettingsSectionCard";
import { StatusMappingSection } from "./components/StatusMappingSection";

interface JiraSourceSettingsProps {
  editingConnection: ConnectionEditState;
  onUpdate: ConnectionEditStateUpdater;
  sectionOffset: number;
  boardStatuses: Status[];
}

export const JiraSourceSettings: React.FC<JiraSourceSettingsProps> = ({
  editingConnection,
  onUpdate,
  sectionOffset,
  boardStatuses,
}) => {
  const { t } = useTranslation();
  const { boardId } = useParams<{ boardId?: string }>();
  const api = useApi();
  const text = useJiraSourceSettingsText(t);
  const vm = useJiraSourceSettingsViewModel({
    boardId,
    editingConnection,
    onUpdate,
    sourceSettingsApi: getSourceSettingsApiAccessor(api),
    t,
  });

  const [resourceQuery, setResourceQuery] = useState("");
  const [projectsQuery, setProjectsQuery] = useState("");
  const [issueTypesQuery, setIssueTypesQuery] = useState("");
  const [statusesToLoadQuery, setStatusesToLoadQuery] = useState("");
  const [doneStatusQuery, setDoneStatusQuery] = useState("");

  if (!vm.isVisible) {
    return null;
  }

  return (
    <>
      <SettingsSectionCard
        sectionOffset={sectionOffset}
        title={t(
          "board.connections.catalogue.sourceSettings.jira.sectionSource",
        )}
      >
        {vm.resources.length > 1 && (
          <Field orientation="horizontal" label={text.resourceLabelText}>
            <SourceSingleTagPicker
              selectedId={
                vm.selectedResourceId === vm.noneOptionValue
                  ? undefined
                  : vm.selectedResourceId
              }
              selectedLabel={
                vm.resources.find(
                  (resource) => resource.id === vm.selectedResourceId,
                )?.name
              }
              query={resourceQuery}
              placeholder={text.resourcePlaceholderText}
              options={vm.resources.map((resource) => ({
                id: resource.id,
                label: resource.name || resource.id,
              }))}
              onQueryChange={setResourceQuery}
              onSelect={vm.onSelectResource}
              clearSelectionText={text.resourcePlaceholderText}
              isLoading={!vm.resourcesLoaded}
            />
          </Field>
        )}

        {vm.selectedResourceId !== vm.noneOptionValue && (
          <>
            <Field
              orientation="horizontal"
              label={text.projectsLabelText}
              hint={text.projectsHintText}
            >
              <SourceMultiTagPicker
                selectedIds={vm.selectedProjectsIds}
                query={projectsQuery}
                placeholder={text.projectsPlaceholderText}
                options={vm.projects.map((project) => ({
                  id: project.id,
                  label: project.name || project.id,
                }))}
                onQueryChange={setProjectsQuery}
                onSelectionChange={vm.onSelectProjects}
                minQueryLength={0}
                typeToSearchText={text.projectsHintText}
                isLoading={!vm.projectsLoaded}
              />
            </Field>

            <Field
              orientation="horizontal"
              label={text.issueTypesLabelText}
              hint={text.issueTypesHintText}
            >
              <SourceMultiTagPicker
                selectedIds={vm.selectedIssueTypeIds}
                query={issueTypesQuery}
                placeholder={text.issueTypesPlaceholderText}
                options={vm.issueTypes.map((issueType) => ({
                  id: issueType.id,
                  label: issueType.name || issueType.id,
                }))}
                onQueryChange={setIssueTypesQuery}
                onSelectionChange={vm.onSelectIssueTypes}
                minQueryLength={0}
                typeToSearchText={text.issueTypesHintText}
                isLoading={!vm.issueTypesLoaded}
              />
            </Field>

            <Field
              orientation="horizontal"
              label={text.statusesToLoadLabelText}
              hint={text.statusesToLoadHintText}
            >
              <SourceMultiTagPicker
                selectedIds={vm.selectedStatusesToLoadIds}
                query={statusesToLoadQuery}
                placeholder={text.statusesToLoadPlaceholderText}
                options={vm.statuses.map((status) => ({
                  id: status.id,
                  label: status.name || status.id,
                }))}
                onQueryChange={setStatusesToLoadQuery}
                onSelectionChange={vm.onSelectStatusesToLoad}
                minQueryLength={0}
                typeToSearchText={text.statusesToLoadHintText}
                isLoading={!vm.statusesLoaded}
              />
            </Field>

            <Field
              orientation="horizontal"
              label={text.doneStatusLabelText}
              hint={text.doneStatusHintText}
              validationMessage={vm.statusReferenceErrorText || undefined}
            >
              <SourceSingleTagPicker
                selectedId={
                  vm.selectedDoneStatusId === vm.noneOptionValue
                    ? undefined
                    : vm.selectedDoneStatusId
                }
                selectedLabel={
                  vm.statuses.find(
                    (status) => status.id === vm.selectedDoneStatusId,
                  )?.name
                }
                query={doneStatusQuery}
                placeholder={text.doneStatusPlaceholderText}
                options={vm.statuses.map((status) => ({
                  id: status.id,
                  label: status.name || status.id,
                }))}
                onQueryChange={setDoneStatusQuery}
                onSelect={vm.onSelectDoneStatus}
                clearSelectionText={text.doneStatusPlaceholderText}
                isLoading={!vm.statusesLoaded}
              />
            </Field>

            <Field orientation="horizontal" label={text.assignedToMeLabelText}>
              <Checkbox checked={vm.assignedToMeFixed} disabled />
            </Field>
          </>
        )}
      </SettingsSectionCard>

      {vm.selectedResourceId !== vm.noneOptionValue && (
        <SettingsSectionCard
          sectionOffset={sectionOffset + 1}
          title={t(
            "board.connections.catalogue.sourceSettings.jira.sectionDisplay",
          )}
        >
          <Field
            orientation="horizontal"
            label={text.includeProjectInTitleLabelText}
          >
            <Checkbox
              checked={vm.includeProjectInTitle}
              onChange={(_, data) =>
                vm.onToggleIncludeProjectInTitle(Boolean(data.checked))
              }
            />
          </Field>

          <Field
            orientation="horizontal"
            label={text.includeTypeAcronymInTitleLabelText}
          >
            <Checkbox
              checked={vm.includeTypeAcronymInTitle}
              onChange={(_, data) =>
                vm.onToggleIncludeTypeAcronymInTitle(Boolean(data.checked))
              }
            />
          </Field>
        </SettingsSectionCard>
      )}

      {(!vm.resourcesLoaded ||
        !vm.projectsLoaded ||
        !vm.issueTypesLoaded ||
        !vm.statusesLoaded) && <Text>{text.loadingText}</Text>}

      {vm.hasError && <Text>{text.loadErrorText}</Text>}

      {vm.selectedResourceId !== vm.noneOptionValue &&
        vm.selectedStatusesToLoadIds.length > 0 && (
          <StatusMappingSection
            sectionNumber={sectionOffset + 2}
            items={vm.statuses
              .filter((s) => vm.selectedStatusesToLoadIds.includes(s.id))
              .map((s) => ({ id: s.id, name: s.name || s.id }))}
            mappings={
              ((editingConnection.configuration as Record<string, unknown>)
                ?.statusMappings as SourceStatusMapping[] | undefined) ?? []
            }
            boardStatuses={boardStatuses}
            onChange={(mappings) =>
              onUpdate({
                configuration: {
                  ...(editingConnection.configuration as Record<string, unknown>),
                  statusMappings: mappings,
                },
              })
            }
          />
        )}
    </>
  );
};
